import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSettings } from "../contexts/SettingsContext";

export default function Home() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4)));
  }, []);

  const heroImage = settings.hero_image;
  const heroVideo = settings.hero_video;
  const heroVideoWebm = settings.hero_video_webm;
  const heroPoster = settings.hero_video_poster || settings.hero_image;
  const aboutImage = settings.about_image;
  const atelierVideo = settings.atelier_video;
  const atelierVideoWebm = settings.atelier_video_webm;
  const atelierPoster = settings.atelier_video_poster;
  const ctaImage = settings.cta_image;

  return (
    <div className="text-text">
      {/* HERO */}
      <section className="relative h-screen min-h-[700px] w-full overflow-hidden">
        {heroVideo ? (
          <video
            poster={heroPoster || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-video"
          >
            {heroVideoWebm && <source src={heroVideoWebm} type="video/webm" />}
            <source src={heroVideo} type="video/mp4" />
          </video>
        ) : (
          heroImage && <img src={heroImage} alt={settings.brand_name} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(12,12,12,0.35) 0%, rgba(12,12,12,0.55) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6 md:px-12">
          {settings.hero_eyebrow && (
            <p className="text-xs uppercase tracking-[0.4em] text-gold mb-6 animate-fade-up">{settings.hero_eyebrow}</p>
          )}
          <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-8xl leading-[0.95] max-w-4xl animate-fade-up" style={{ animationDelay: "100ms" }}>
            {settings.hero_title_line_1}<br /><em className="not-italic text-gold">{settings.hero_title_line_2}</em>
          </h1>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "200ms" }}>
            <Link to="/shop" className="btn-gold" data-testid="hero-shop-btn">{settings.hero_cta_label || "Discover the Collection"}</Link>
            <Link to="/about" className="btn-outline">The Atelier</Link>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden py-8 border-y border-[rgba(244,244,240,0.06)]">
        <div className="flex animate-marquee whitespace-nowrap text-2xl font-serif text-muted">
          {["ETHEREAL-1","CONSIDERED-1","TIMELESS-1","SCULPTED-1","ETHEREAL-2","CONSIDERED-2","TIMELESS-2","SCULPTED-2"].map((w) => (
            <span key={w} className="mx-8">{w.split("-")[0]} <span className="text-gold mx-2">✦</span></span>
          ))}
        </div>
      </div>

      {/* FEATURED */}
      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">The Edit</p>
            <h2 className="font-serif text-4xl md:text-6xl">Pieces of the season</h2>
          </div>
          <Link to="/shop" className="nav-link">View All →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {featured.map(p => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        <div className="lg:col-span-5 lg:col-start-1">
          {aboutImage && <img src={aboutImage} alt="GOSHI Atelier" className="w-full h-[600px] object-cover" />}
        </div>
        <div className="lg:col-span-5 lg:col-start-7 space-y-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{settings.about_eyebrow}</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">{settings.about_title}</h2>
          <div className="divider-gold" />
          <p className="text-muted leading-relaxed max-w-md whitespace-pre-line">{settings.about_body}</p>
          <Link to="/about" className="nav-link inline-block pt-2">Read Our Story →</Link>
        </div>
      </section>

      {/* BEHIND THE ATELIER — VIDEO SECTION (only renders when a video is set) */}
      {atelierVideo && (
        <section className="px-6 md:px-12 py-24 md:py-32 bg-[#0a0a0a] border-y border-[rgba(244,244,240,0.05)]" data-testid="atelier-video-section">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 space-y-5 order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">In motion</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">{settings.atelier_video_title || "Behind the atelier"}</h2>
              <div className="divider-gold" />
              <p className="text-muted leading-relaxed max-w-md whitespace-pre-line">{settings.atelier_video_body}</p>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative aspect-[16/10] w-full overflow-hidden">
                <video
                  poster={atelierPoster || undefined}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="atelier-video"
                >
                  {atelierVideoWebm && <source src={atelierVideoWebm} type="video/webm" />}
                  <source src={atelierVideo} type="video/mp4" />
                </video>
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(12,12,12,0) 60%, rgba(12,12,12,0.35) 100%)" }} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA BANNER */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        {ctaImage && <img src={ctaImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />}
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">{settings.cta_eyebrow}</p>
          <h2 className="font-serif text-4xl md:text-6xl max-w-3xl">{settings.cta_title}</h2>
          {settings.cta_subtitle && (
            <p className="text-muted mt-5 max-w-lg whitespace-pre-line">
              {settings.cta_subtitle.split(settings.cta_coupon_code || "##").map((part, i, arr) =>
                i < arr.length - 1
                  ? <span key={`cta-part-${i}-with-code`}>{part}<span className="text-gold tracking-[0.2em]">{settings.cta_coupon_code}</span></span>
                  : <span key={`cta-part-${i}-tail`}>{part}</span>
              )}
            </p>
          )}
          <Link to="/shop" className="btn-gold mt-8">Shop Now</Link>
        </div>
      </section>
    </div>
  );
}
